import { useState } from 'react';
import { Checkbox, PopoverMenu } from '..';
import classNames from 'classnames';
import DropdownArrow from '@/assets/caretDownLight.svg';

type DateOption = {
  label: string;
  id: string;
  children?: Array<{ label: string; id: string }>;
};

interface EventHierarchicalDateFilterProps {
  value: string[];
  name: string;
  options: DateOption[];
  onChange: (value: string | string[]) => void;
  onClear: () => void;
}

const EventHierarchicalDateFilter = ({ value, name, options, onChange, onClear }: EventHierarchicalDateFilterProps) => {
  const [expandedYears, setExpandedYears] = useState<string[]>([]);

  const toggleYear = (yearId: string) => {
    setExpandedYears((prev) => (prev.includes(yearId) ? prev.filter((id) => id !== yearId) : [...prev, yearId]));
  };

  const isYearChecked = (year: DateOption) => {
    if (!year.children) return false;
    return year.children.every((month) => value.includes(month.id));
  };

  const isYearIndeterminate = (year: DateOption) => {
    if (!year.children) return false;
    const checkedCount = year.children.filter((month) => value.includes(month.id)).length;
    return checkedCount > 0 && checkedCount < year.children.length;
  };

  const handleYearChange = (year: DateOption) => {
    if (!year.children) return;
    const allMonthIds = year.children.map((m) => m.id);
    const allChecked = isYearChecked(year);
    const isIndeterminate = isYearIndeterminate(year);

    if (allChecked || isIndeterminate) {
      onChange(value.filter((id) => !allMonthIds.includes(id)));
    } else {
      onChange([...value, ...allMonthIds]);
    }
  };

  const hasSelection = value.length > 0;

  return (
    <PopoverMenu
      title={name}
      className={classNames(
        '[&_.main-button]:h-10 [&_.main-button]:rounded-4xl [&_.main-button]:border [&_.main-button]:border-black/10 [&_.main-button]:px-4 [&.open_.main-button]:bg-white',
        '[&_.main-button]:min-w-[200px]',
        '[&_.content]:min-w-[200px] [&_.content]:rounded-3xl [&_.content]:border [&_.content]:border-black/10 [&_.content]:shadow-lg',
        'w-full md:w-auto [&_.main-button]:justify-center [&_.main-button]:md:justify-between',
      )}
      titleClass="text-sm font-medium"
      offset={10}
      placement="bottom-start"
      showClearIcon={hasSelection}
      onClearIconClick={onClear}
    >
      <div className="flex max-h-80 min-w-max grow flex-col gap-4 overflow-y-auto p-4">
        {options.map((year) => (
          <div key={year.id}>
            <div className="flex items-center gap-2">
              {year.children && (
                <button
                  onClick={() => toggleYear(year.id)}
                  className="h-full cursor-pointer text-sm text-gray-600 hover:text-gray-900"
                >
                  <DropdownArrow
                    className={classNames('h-4 w-4 lg:h-3 lg:w-3', expandedYears.includes(year.id) && '-rotate-90')}
                  />
                </button>
              )}
              <Checkbox
                label={year.label}
                checked={isYearChecked(year)}
                indeterminate={isYearIndeterminate(year)}
                onChange={() => handleYearChange(year)}
              />
            </div>
            {year.children && expandedYears.includes(year.id) && (
              <div className="mt-4 ml-10 flex flex-col gap-4">
                {year.children.map((month) => (
                  <Checkbox
                    key={month.id}
                    label={month.label}
                    checked={value.includes(month.id)}
                    onChange={() => onChange(month.id)}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </PopoverMenu>
  );
};

export default EventHierarchicalDateFilter;
